/**
 * Marketplace Messages Page - List of conversations
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, Loader2, ArrowRight, Package, Clock } from 'lucide-react';
import { api } from '../../../services/api';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function Messages() {
  const { t } = useLanguage();
  const { data, isLoading, error } = useQuery({
    queryKey: ['marketplace-conversations'],
    queryFn: () => api.getMarketplaceConversations({ limit: 50 }),
  });

  const conversations = data?.data || [];

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('messages.title', 'Messages')}</h1>
          <p className="text-gray-600 mt-1">
            {t('messages.subtitle', 'Your marketplace conversations')}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {t('messages.loadError', 'Failed to load conversations. Please try again.')}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && conversations.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('messages.noMessagesTitle', 'No messages yet')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('messages.noMessagesDescription', 'When you contact a seller or someone contacts you about a listing, your conversations will appear here.')}
          </p>
          <Link
            to="/account/marketplace"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            {t('messages.viewListings', 'View your listings')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Conversations List */}
      {!isLoading && !error && conversations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {conversations.map((conversation) => {
            const hasUnread = (conversation.unread_count || 0) > 0;

            return (
              <Link
                key={conversation.id}
                to={`/account/marketplace/messages/${conversation.id}`}
                className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${
                  hasUnread ? 'bg-primary-50' : ''
                }`}
              >
                {/* Listing Image */}
                <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {conversation.listing?.photos?.[0] ? (
                    <img
                      src={conversation.listing.photos[0]}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`font-medium truncate ${hasUnread ? 'text-gray-900' : 'text-gray-700'}`}>
                      {conversation.other_party?.email?.split('@')[0] || 'User'}
                    </p>
                    <span className="text-xs text-gray-500 flex-shrink-0 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(conversation.last_message_at)}
                    </span>
                  </div>

                  <p className="text-sm text-gray-500 truncate">
                    {conversation.listing?.product_code}
                    {conversation.listing?.asking_price && (
                      <span className="ml-1">
                        · ${conversation.listing.asking_price}
                      </span>
                    )}
                  </p>

                  {conversation.last_message_content && (
                    <p className={`text-sm truncate mt-1 ${hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {conversation.last_message_content}
                    </p>
                  )}
                </div>

                {/* Unread Badge */}
                {hasUnread && (
                  <div className="w-6 h-6 bg-primary-600 text-white text-xs font-medium rounded-full flex items-center justify-center flex-shrink-0">
                    {conversation.unread_count}
                  </div>
                )}

                <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
